import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, combineLatest, EMPTY, filter, first, switchMap } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { LoggerService } from '../logger/logger.service';
import { ProfileQuery } from '../profile/state/profile.query';
import { PermissionsService } from '../auth/permissions.service';
import { CustomFieldsV2Service } from '../company-settings/custom-fields-v2/custom-fields-v2.service';
import { RolePermissionsHelper } from '../auth/role-permmissions-helper';

@Injectable({ providedIn: 'root' })
export class CustomFieldsBootstrapService {

	private readonly loggerName = 'CustomFieldsBootstrapService';
	private readonly destroyRef = inject(DestroyRef);
	private readonly logger = inject(LoggerService);
	private readonly authService = inject(AuthService);
	private readonly profileQuery = inject(ProfileQuery);
	private readonly permissionsService = inject(PermissionsService);
	private readonly customFieldsV2Service = inject(CustomFieldsV2Service);

	init(): void {
		combineLatest([
			this.authService.isLoggedIn$,
			this.profileQuery.select('verifiedToCompany'),
		]).pipe(
			filter(([loggedIn, verified]) => !!loggedIn && verified === true),
			first(),
			switchMap(() => {
				const isCustomFieldsAllowed = this.permissionsService.getIsAllowed(RolePermissionsHelper.getPerrmitedRoleForCustomFields());

				// Unified custom-fields-v2 has no separate custom-values resource — it loads custom fields only.
				// Skip entirely for roles that don't consume custom fields.
				return isCustomFieldsAllowed ? this.customFieldsV2Service.init() : EMPTY;
			}),
			catchError(err => this.handleInitError(err)),
			takeUntilDestroyed(this.destroyRef),
		).subscribe();
	}

	private handleInitError(err: unknown): typeof EMPTY {
		this.logger.error(this.loggerName, `Custom fields init failed: ${(err as Error)?.message ?? err}`);
		return EMPTY;
	}
}
